import React, { useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MapPin, Check, ShieldCheck, Clock, CircleDollarSign, CalendarCheck } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { useReveal } from "../hooks/useReveal";
import BookingCalendar from "../components/BookingCalendar";
import { business, jobTypes } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const trust = [
  { icon: CircleDollarSign, label: "Free, no-obligation quotes" },
  { icon: Clock, label: "Same-day response, most days" },
  { icon: ShieldCheck, label: "Fully insured, owner operated" },
];

export default function Quote() {
  const ref = useReveal();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState({
    date: searchParams.get("date") || "",
    time: searchParams.get("time") || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    suburb: "",
    jobType: "",
    details: "",
  });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.jobType) {
      toast({
        title: "A few details missing",
        description: "Please add your name, phone and the type of job.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, {
        ...form,
        bookingDate: booking.date,
        bookingTime: booking.time,
      });
      setSubmitting(false);
      setDone(true);
      toast({
        title: "Quote request sent",
        description: "Thanks, we\u2019ll be in touch shortly with a price.",
      });
    } catch (err) {
      setSubmitting(false);
      toast({
        title: "Something went wrong",
        description: "Please try again, or call us on " + business.phone + ".",
      });
    }
  };

  return (
    <main ref={ref} className="bg-brand-stone">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-28 sm:pt-36 pb-20 sm:pb-28">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-brand-ink/60 hover:text-brand-ink transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>

        {!done && !booking.date ? (
          <div className="reveal mt-8 rounded-3xl border border-black/10 bg-brand-stone/50 p-5 sm:p-8">
            <div className="flex flex-col gap-1.5">
              <span className="kicker text-brand-teal">Pick a time</span>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                When would suit you?
              </h2>
              <p className="max-w-xl text-sm text-brand-ink/60">
                Green days are free to book. Choose a day and a start time, then add your
                details below. Prefer to skip this? Just fill in the form.
              </p>
            </div>
            <div className="mt-6">
              <BookingCalendar
                confirmLabel="Use this time"
                onConfirm={(date, time) => {
                  setBooking({ date, time });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </div>
          </div>
        ) : null}

        <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Left: intro + reassurance */}
          <div className="lg:col-span-5">
            <span className="kicker text-brand-teal reveal">Request a quote</span>
            <h1 className="reveal mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.02]">
              Tell us about the job.
            </h1>
            <p className="reveal mt-5 max-w-md text-brand-ink/70 leading-relaxed">
              Fill in the details below and you’ll get a straight answer and a fair
              price. Not sure of the specifics? No worries, just describe it and we&rsquo;ll
              work it out.
            </p>

            <ul className="reveal mt-8 space-y-4">
              {trust.map((t) => (
                <li key={t.label} className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-tealsoft">
                    <t.icon className="h-4.5 w-4.5 text-brand-tealdark" strokeWidth={2} />
                  </span>
                  <span className="text-sm font-medium text-brand-ink/80">{t.label}</span>
                </li>
              ))}
            </ul>

            <div className="reveal mt-9 space-y-3 border-t border-black/10 pt-7 text-sm">
              <a href={business.phoneHref} className="flex items-center gap-3 font-medium hover:text-brand-teal transition-colors">
                <Phone className="h-4 w-4 text-brand-teal" /> {business.phone}
              </a>
              <a href={`mailto:${business.email}`} className="flex items-center gap-3 font-medium hover:text-brand-teal transition-colors">
                <Mail className="h-4 w-4 text-brand-teal" /> {business.email}
              </a>
              <p className="flex items-center gap-3 text-brand-ink/70">
                <MapPin className="h-4 w-4 text-brand-teal" /> {business.area}, {business.region}
              </p>
            </div>
          </div>

          {/* Right: form card */}
          <div className="lg:col-span-7">
            <div className="reveal rounded-3xl border border-black/8 bg-white p-6 sm:p-10 shadow-xl shadow-black/5">
              {done ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-tealsoft">
                    <Check className="h-8 w-8 text-brand-teal" strokeWidth={2.5} />
                  </span>
                  <h2 className="mt-6 font-display text-2xl font-semibold">Request received</h2>
                  <p className="mt-3 max-w-sm text-brand-ink/60">
                    Thanks {form.name.split(" ")[0]}. We’ve got your details and will be
                    in touch shortly with a price. For anything urgent, give us a call.
                  </p>
                  <div className="mt-8 flex gap-3">
                    <a href={business.phoneHref} className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-6 py-3 text-sm font-semibold text-white hover:bg-brand-tealdark transition-colors">
                      <Phone className="h-4 w-4" /> Call now
                    </a>
                    <Link to="/" className="inline-flex items-center rounded-full border border-black/15 px-6 py-3 text-sm font-semibold hover:border-black/40 transition-colors">
                      Back home
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {booking.date ? (
                    <div className="flex items-start gap-3 rounded-xl border border-brand-teal/30 bg-brand-tealsoft px-4 py-3">
                      <CalendarCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-tealdark" strokeWidth={2.2} />
                      <div className="flex-1 text-sm">
                        <p className="font-semibold text-brand-tealdark">Preferred slot selected</p>
                        <p className="text-brand-ink/70">
                          {booking.date}{booking.time ? ` at ${booking.time}` : ""} &middot; we&rsquo;ll confirm with your quote.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setBooking({ date: "", time: "" })}
                        className="shrink-0 text-xs font-semibold text-brand-tealdark underline underline-offset-2 hover:no-underline"
                      >
                        Change
                      </button>
                    </div>
                  ) : null}
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">Your name *</Label>
                      <Input id="name" value={form.name} onChange={update("name")} placeholder="e.g. Muamet" className="h-12 bg-brand-stone/60" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input id="phone" value={form.phone} onChange={update("phone")} placeholder="04xx xxx xxx" className="h-12 bg-brand-stone/60" />
                    </div>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className="h-12 bg-brand-stone/60" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="suburb">Suburb</Label>
                      <Input id="suburb" value={form.suburb} onChange={update("suburb")} placeholder="e.g. Scarborough" className="h-12 bg-brand-stone/60" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Type of job *</Label>
                    <Select value={form.jobType} onValueChange={update("jobType")}>
                      <SelectTrigger className="h-12 bg-brand-stone/60">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {jobTypes.map((j) => (
                          <SelectItem key={j} value={j}>{j}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="details">Job details</Label>
                    <Textarea
                      id="details"
                      value={form.details}
                      onChange={update("details")}
                      placeholder="Rough dimensions, what needs cutting or removing, access, timing…"
                      className="min-h-[130px] bg-brand-stone/60 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-ink px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-brand-teal disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Send quote request"}
                  </button>
                  <p className="text-center text-xs text-brand-ink/40">
                    We’ll only use your details to get back to you about this job.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
